/**
 * SuggestSourceModal — iter86: in-app source suggestion form.
 * Opens from the feed placeholder CTA; saves to the backend
 * (POST /api/source-suggestions) for review in the Admin → Suggestions tab.
 * No email involved. `trigger` is any node (rendered via DialogTrigger asChild).
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../config";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { Loader2, Send } from "lucide-react";

export const SuggestSourceModal = ({ darkMode = false, trigger }) => {
  const dm = darkMode;
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (url.trim().length < 4 || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/source-suggestions`, { url: url.trim(), note: note.trim() });
      toast.success(r.data.duplicate
        ? "You already suggested this source — it's in our review queue."
        : "Thanks! Our editorial team will review it.");
      setUrl(""); setNote(""); setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the suggestion — please retry.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className={dm ? "bg-[#1a2122] border-[#252e2f]" : "bg-white"} data-testid="suggest-source-modal">
        <DialogHeader>
          <DialogTitle className={`editorial-heading ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Suggest a source</DialogTitle>
          <DialogDescription className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            Know a great travel-industry source we should track? Drop the link below.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-1">
          <Input
            data-testid="suggest-source-url-input"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://example.com or example.com/blog"
            maxLength={500}
            className={`border-0 ${dm ? "bg-[#252e2f] text-slate-200 placeholder:text-slate-600" : "bg-[#f3f4f5]"}`}
          />
          <textarea
            data-testid="suggest-source-note-input"
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Why is it worth tracking? (optional)"
            maxLength={500}
            rows={3}
            className={`w-full rounded-md px-3 py-2 text-sm outline-none resize-none ${dm ? "bg-[#252e2f] text-slate-200 placeholder:text-slate-600" : "bg-[#f3f4f5] text-[#191c1d] placeholder:text-[#9aa7a9]"}`}
          />
          <button
            type="submit"
            data-testid="suggest-source-submit"
            disabled={busy || url.trim().length < 4}
            className={`flex items-center gap-2 px-5 py-2 rounded-full category-tag disabled:opacity-40 ${dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white"}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {busy ? "Sending…" : "Submit suggestion"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SuggestSourceModal;
